/* eslint-disable max-lines */
import React from 'react';
import ReactDOM from 'react-dom/client';
import '@assets/css/index.css';
// Side-effect: patches react-hot-toast to dedupe identical messages by auto-id.
// Imported before App so the patch is applied before any component fires a toast.
import '@utils/toast-dedup.ts';
import { installStaleBuildRecovery } from '@utils/stale-build-recovery.ts';
import App from './app.tsx';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

if (import.meta.env.VITE_NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://b80070262e26d214eab41c6b976c7174@o191730.ingest.us.sentry.io/4509729194835968',
    // Must match the release name the vite plugin uploads sourcemaps under.
    // Both are set to github.sha in CI via SENTRY_RELEASE / VITE_SENTRY_RELEASE.
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
      // Performance monitoring with minimal overhead
      Sentry.browserTracingIntegration(),
      // Session replay - only on errors
      Sentry.replayIntegration({
        // Mask all text and inputs by default for privacy
        maskAllText: false,
        maskAllInputs: true,
        // Sample rate for network requests/responses in replay
        networkDetailAllowUrls: [/^https:\/\/api.scriptix\.io/],
        // Capture console logs in replay
        useCompression: true
      }),
      // Breadcrumbs for better context
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        sentry: true,
        xhr: true
      }),
      // HTTP context
      Sentry.httpContextIntegration(),
      // Dedupe integration to avoid duplicate errors
      Sentry.dedupeIntegration(),
      // Function names in stack traces
      Sentry.functionToStringIntegration(),
      // Extra context about the user's browser
      Sentry.contextLinesIntegration(),
      // Report unhandled promise rejections
      Sentry.linkedErrorsIntegration(),
      // Capture console errors (but filtered in beforeSend)
      Sentry.captureConsoleIntegration({
        levels: ['error'] // Only capture errors, not warnings
      })
    ],
    // Tracing - significantly reduced to save costs
    tracesSampleRate: 0.01, // Only capture 1% of transactions for performance monitoring
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api.scriptix\.io/,
      /^https:\/\/scriptix\.app/
    ],
    // Session Replay - optimized for cost
    replaysSessionSampleRate: 0, // Don't record any regular sessions
    replaysOnErrorSampleRate: 1.0, // Always record sessions when errors occur
    // Error sampling
    sampleRate: 1.0, // Capture 100% of errors (you want all errors)
    // Ignore certain errors to reduce noise
    ignoreErrors: [
      // Ignore common non-actionable errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      // Sentry's newer synthesized wording for Promise rejections where the
      // reason isn't an Error (plain `{code, message}` objects etc.). These
      // almost exclusively come from browser extensions and password-manager
      // / anti-fraud scripts injected into the login form — we can't fix
      // third-party code and the user isn't blocked.
      /Object captured as promise rejection with keys/,
      // AG Grid deprecation warnings from browser extensions (not from our code)
      /AG Grid:/,
      // Slate editor DOM resolution errors - benign race conditions
      'Cannot resolve a DOM node from Slate node',
      'Cannot resolve a DOM point from Slate point',
      /Failed to execute '(setEnd|setStart|collapse)' on 'Range'/,
      'There is no child at offset',
      /IndexSizeError.*Range/,
      // Slate DOM↔state resolution races — same family as the two above,
      // thrown by slate-react's internal onClick/selection handlers when
      // the mapping is briefly stale (e.g. right after a split rewrites
      // paragraph nodes). The guard in slate-editor-utils.ts preempts
      // these in the transcript editors; this covers any other Slate
      // surface that slips through.
      'Unable to find the path for Slate node',
      /Cannot resolve a Slate (?:node|point|range) from (?:a )?DOM/,
      // Backend text nodes serialize with `children: null`; Slate's split_node /
      // set_node operation handlers throw on this because `children` is in
      // NON_SETTABLE_NODE_PROPERTIES. withErrorHandling() in slate-editor-utils
      // swallows it at the onChange boundary so the editor stays usable; this
      // covers anything that fires outside that boundary (e.g. selection
      // reconciliation after the aborted op).
      'Cannot set the "children" property of nodes',
      // Registration validation errors - expected 4xx responses handled by UI
      'Registration could not be completed',
      /RegistrationValidationError/,
      // HTTP errors that are handled by the application
      'Request failed with status code 400', // Bad request - validation errors, handled by forms
      'Request failed with status code 401', // Session expired - handled by auth context
      'Request failed with status code 402', // Payment required - out of credits/subscription, handled by UI
      'Request failed with status code 403', // Forbidden - permission denied, handled by UI
      /AxiosError:.*403/, // AxiosError format with 403 status
      'Request failed with status code 404', // Not found - handled by UI
      /AxiosError:.*404/, // AxiosError format with 404 status
      'Request failed with status code 422', // Unprocessable - validation errors, handled by forms/UI
      /AxiosError:.*422/, // AxiosError format with 422 status
      'Request failed with status code 429', // Too many requests - rate limiting, handled by UI
      'Request failed with status code 409', // Conflict - e.g., folder has active sessions, handled by UI
      // Dynamic import errors - occur when deployment changes chunk hashes while
      // user has old version cached. Still filtered as raw noise: the actionable
      // signal is the `stale_build` tagged event from stale-build-recovery.ts,
      // which reports every occurrence plus whether the reload actually fixed it.
      'error loading dynamically imported module',
      /Loading chunk .* failed/,
      /Failed to fetch dynamically imported module/,
      // Network errors that are usually not actionable
      'NetworkError',
      'Network Error', // Axios network error (connection lost, timeout, etc.)
      'Network request failed',
      // Timeout errors - usually user connection issues or tab left open
      /timeout of \d+ms exceeded/,
      // Request aborted - user navigated away or closed tab during request
      'Request aborted',
      'AbortError',
      'aborted by the user agent',
      'Network error during upload', // Azure Blob upload failures (user network issues)
      'Failed to fetch',
      'Upload aborted', // User cancelled upload
      // S3 multipart part PUT dropped mid-flight. multipart-upload.ts retries
      // in place; this message only surfaces here when retries are exhausted,
      // i.e. the user's network is sustainedly flaky. The frontend already
      // POSTs /upload-failed so the session is marked, and the UI shows a
      // retry affordance.
      'Upload failed: the connection was interrupted',
      // Uppy/TUS upload errors - handled in UI with retry option
      'This looks like a network error',
      'endpoint might be blocked',
      'tus: failed to upload',
      'tus: failed to resume upload',
      'tus: unexpected response while uploading chunk',
      /\[Uppy\].*tus:/,
      // Uppy Companion provider errors — thrown when the Companion server is
      // briefly unreachable while a user clicks a provider tab (OneDrive,
      // Google Drive, Dropbox, SharePoint, etc.) in the upload dashboard.
      // Uppy shows a retry banner inside the panel; Companion uptime is
      // monitored separately, so Sentry gets no additional signal here.
      /Could not (?:GET|POST|PUT|DELETE) https?:\/\/companion\./,
      // OAuth popup closed/aborted by user - expected behavior for OneDrive, SharePoint, etc.
      'Auth window was closed by the user',
      /[Aa]uth window was closed/,
      'auth aborted',
      /[Aa]uth aborted/,
      // Microphone / screen-capture not supported — browser/device limitation, not a bug
      'Microphone access not supported',
      'Screen recorder access not supported',
      'Screen capture is not supported',
      /getDisplayMedia is not a function/,
      /this\.mediaDevices\.getDisplayMedia/,
      // Android WebView teardown — postMessage to a destroyed Java bridge
      'Error invoking postMessage',
      /callWebView.*Java object is gone/,
      /Java object is gone/,
      // Old Safari / legacy WebView polyfill gaps — lower build target or add polyfill
      'Object.hasOwn is not a function',
      /\.at is not a function/,
      /\.entries\.at is not a function/,
      // HeadlessUI internal warnings (not actionable)
      'Failed to get offset for index',
      // LocalStorage quota exceeded or read-only filesystem (private browsing,
      // full/corrupted device storage) - OS/device limitation, not actionable
      'QuotaExceededError',
      /exceeded the quota/,
      'NoModificationAllowedError',
      /could not be modified due to the state of the underlying filesystem/,
      // Sentry Session Replay uses MediaRecorder internally; on Safari and some
      // Firefox/mobile builds the MIME type it selects isn't supported, causing
      // this NotSupportedError.  It originates in Sentry's own code, not ours.
      "Failed to execute 'start' on 'MediaRecorder'",
      /MediaRecorder.*start/,
      // Browser out of memory - user's device resource limitation, not actionable
      'NS_ERROR_OUT_OF_MEMORY',
      'out of memory',
      /OutOfMemoryError/,
      // Ignore errors from browser extensions
      'chrome-extension://',
      'moz-extension://',
      'safari-extension://',
      // Ignore common third-party errors
      'top.GLOBALS',
      'Non-Error exception captured',
      'Script error',
      // Safari-specific errors from browser extensions or third-party scripts
      "Can't find variable:",
      'EmptyRanges',
      // React DOM synchronization errors - caused by browser extensions, third-party scripts,
      // or rapid navigation that causes React's virtual DOM to get out of sync with actual DOM
      "Failed to execute 'removeChild' on 'Node'",
      "Failed to execute 'insertBefore' on 'Node'",
      "Failed to execute 'appendChild' on 'Node'",
      'The node to be removed is not a child of this node',
      'NotFoundError'
    ],
    denyUrls: [
      // Ignore browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^file:\/\//i,
      // Ignore third-party scripts
      /graph\.facebook\.com/i,
      /connect\.facebook\.net/i,
      /google-analytics\.com/i
    ],
    // Before send - filter out non-critical errors
    beforeSend(event, hint) {
      // Sentry Issues is for actionable errors only. Warnings, info and debug
      // events are noise — drop them before anything else. Sentry defaults
      // captureException() and unhandled errors to level `error`, so real
      // errors still pass through; only explicit captureMessage(..., { level:
      // 'warning' | 'info' | 'log' | 'debug' }) calls get filtered here.
      if (event.level && event.level !== 'error' && event.level !== 'fatal') {
        return null;
      }

      // Allowlist for targeted diagnostic captures (e.g. document-save backend
      // rejections). These are explicit captureMessage calls that carry the
      // response body in `extra` — they must bypass the status-based filters
      // below, including the AxiosError-breadcrumb check which would otherwise
      // drop them because the matching XHR appears in the trail.
      if (event.tags?.endpoint === 'update-document') {
        return event;
      }

      // Stale-build downstream suppression. When a long-lived tab tries to
      // load a hashed chunk from an old deploy, the chunk 404s, the module
      // resolves to `undefined`, and anything reading `.default`/other
      // properties off it throws a TypeError. stale-build-recovery.ts kicks in
      // and reloads, but the TypeError fires in the ~100ms before the reload
      // takes effect and reaches Sentry on its way out. Not actionable — the
      // reload fixes it. Drop anything captured while a recovery reload is in
      // flight (sessionStorage `firstAt` within the last 30s).
      try {
        const raw = window.sessionStorage?.getItem(
          'scriptix.stale-build-recovery'
        );
        if (raw) {
          const parsed = JSON.parse(raw) as { firstAt?: number };
          if (
            typeof parsed.firstAt === 'number' &&
            Date.now() - parsed.firstAt < 30_000
          ) {
            return null;
          }
        }
      } catch {
        // Storage unavailable — fall through, treat as normal event.
      }

      // Filter out specific non-critical errors
      if (event.exception) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const error = hint.originalException as any;

        // Check if this is an Axios error with response status
        const status = error?.response?.status || error?.status;

        // Skip 400 errors - validation errors, handled by forms
        if (
          status === 400 ||
          error?.message?.includes('400') ||
          error?.message?.includes('status code 400')
        ) {
          return null;
        }

        // Filter AxiosError objects logged to console
        if (error?.isAxiosError && error?.response?.status === 400) {
          return null;
        }

        // Skip 401 errors - handled by auth context
        if (
          status === 401 ||
          error?.message?.includes('401') ||
          error?.message?.includes('Unauthorized')
        ) {
          return null;
        }

        // Skip 402 errors - payment required, out of credits/subscription, handled by UI
        if (status === 402 || error?.message?.includes('402')) {
          return null;
        }

        // Skip 403 errors - permission denied, handled by application
        if (status === 403 || error?.message?.includes('403')) {
          return null;
        }

        // Skip 404 errors - handled by UI components
        if (
          status === 404 ||
          error?.message?.includes('404') ||
          error?.message?.includes('Not Found')
        ) {
          return null;
        }

        // Skip 422 errors - validation errors, handled by forms/UI (e.g., folder not found, document save validation)
        if (
          status === 422 ||
          error?.message?.includes('422') ||
          error?.message?.includes('Unprocessable') ||
          error?.message?.includes('Request failed with status code 422')
        ) {
          return null;
        }

        // Skip 429 errors - rate limiting, handled by UI
        if (status === 429 || error?.message?.includes('429')) {
          return null;
        }

        // Skip 409 errors - conflict (e.g., folder has active sessions), handled by UI
        if (status === 409 || error?.message?.includes('409')) {
          return null;
        }

        // Skip backend 500s on /folders/* — these are server-side bugs tracked in
        // api-scriptix Sentry; surfacing them here is noise without actionable frontend context.
        if (
          status === 500 &&
          (error?.config?.url?.includes('/folders/') ||
            error?.request?.responseURL?.includes('/folders/'))
        ) {
          return null;
        }

        // Skip network errors that might be user connection issues
        if (
          (error?.message?.includes('Network Error') ||
            error?.message?.includes('Network request failed') ||
            error?.message?.includes('NetworkError')) &&
          !error?.message?.includes('500')
        ) {
          return null;
        }

        // Skip upload-related network errors (user connection issues, not bugs)
        if (
          error?.message?.includes('Network error during upload') ||
          error?.message?.includes('Upload failed with status') ||
          error?.message?.includes('Upload aborted') ||
          error?.message?.includes(
            'Upload failed: the connection was interrupted'
          )
        ) {
          return null;
        }

        // Skip Uppy/TUS errors - these are handled in the UI with retry options
        if (
          error?.message?.includes('This looks like a network error') ||
          error?.message?.includes('endpoint might be blocked') ||
          error?.message?.includes('tus:') ||
          error?.message?.includes('[Uppy]') ||
          error?.message?.includes(
            'unexpected response while uploading chunk'
          ) ||
          // Uppy Companion provider reachability (OneDrive/Drive/Dropbox/SharePoint
          // list calls) — Uppy flags these with isNetworkError and shows a retry
          // banner in the provider panel. See ignoreErrors for the matching
          // regex applied to captureConsoleIntegration events.
          error?.isNetworkError === true ||
          error?.config?.url?.includes('companion.') ||
          error?.message?.includes('companion.scriptix.')
        ) {
          return null;
        }

        // Skip OAuth popup closed/aborted by user - expected user behavior for OneDrive, SharePoint, etc.
        if (
          error?.message?.includes('Auth window was closed') ||
          error?.message?.includes('auth window was closed') ||
          error?.message?.includes('auth aborted') ||
          error?.message?.includes('Auth aborted')
        ) {
          return null;
        }

        // Skip cancelled/aborted requests - user navigated away or stopped media
        if (
          error?.message?.includes('AbortError') ||
          error?.name === 'AbortError' ||
          error?.message?.includes('aborted by the user agent')
        ) {
          return null;
        }

        // Skip timeout errors - usually user connection issues or tab left open too long
        if (
          error?.message?.includes('timeout') &&
          error?.message?.includes('exceeded')
        ) {
          return null;
        }

        // Skip aborted requests - user navigated away or closed tab during request
        if (error?.message?.includes('Request aborted')) {
          return null;
        }

        // Skip localStorage quota exceeded - user's device storage is full
        if (
          error?.name === 'QuotaExceededError' ||
          error?.message?.includes('exceeded the quota') ||
          error?.message?.includes('QuotaExceededError')
        ) {
          return null;
        }

        // Skip browser out of memory errors - user's device resource limitation
        if (
          error?.name === 'NS_ERROR_OUT_OF_MEMORY' ||
          error?.message?.includes('NS_ERROR_OUT_OF_MEMORY') ||
          error?.message?.includes('out of memory') ||
          error?.message?.includes('OutOfMemoryError')
        ) {
          return null;
        }

        // Skip Safari errors from browser extensions or third-party scripts
        if (
          error?.message?.includes("Can't find variable:") ||
          error?.message?.includes('EmptyRanges')
        ) {
          return null;
        }

        // Skip React DOM synchronization errors - caused by browser extensions,
        // third-party scripts, or rapid navigation
        if (
          error?.name === 'NotFoundError' ||
          error?.message?.includes("Failed to execute 'removeChild'") ||
          error?.message?.includes("Failed to execute 'insertBefore'") ||
          error?.message?.includes("Failed to execute 'appendChild'") ||
          error?.message?.includes('is not a child of this node')
        ) {
          return null;
        }

        // Skip Slate.js editor Range errors - benign race conditions during selection/rendering
        if (
          error?.name === 'IndexSizeError' ||
          error?.message?.includes("Failed to execute 'setEnd' on 'Range'") ||
          error?.message?.includes("Failed to execute 'setStart' on 'Range'") ||
          error?.message?.includes('There is no child at offset') ||
          error?.message?.includes('Cannot resolve a DOM node from Slate') ||
          error?.message?.includes('Cannot resolve a DOM point from Slate') ||
          error?.message?.includes('Unable to find the path for Slate node') ||
          /Cannot resolve a Slate (?:node|point|range) from (?:a )?DOM/.test(
            error?.message ?? ''
          ) ||
          error?.message?.includes(
            'Cannot set the "children" property of nodes'
          )
        ) {
          return null;
        }

        // Skip microphone/screen-capture browser limitations — surface as UI messages instead
        if (
          error?.message?.includes('Microphone access not supported') ||
          error?.message?.includes('Screen recorder access not supported') ||
          error?.message?.includes('Screen capture is not supported') ||
          error?.message?.includes('getDisplayMedia is not a function') ||
          error?.message?.includes('mediaDevices.getDisplayMedia')
        ) {
          return null;
        }

        // Skip Android WebView teardown errors — Java bridge destroyed after activity exit
        if (
          error?.message?.includes('Error invoking postMessage') ||
          error?.message?.includes('Java object is gone') ||
          error?.message?.includes('callWebView')
        ) {
          return null;
        }

        // Skip polyfill gaps on old Safari/WebView — not actionable without target change
        if (
          error?.message?.includes('Object.hasOwn is not a function') ||
          error?.message?.includes('.at is not a function') ||
          error?.message?.includes('.entries.at is not a function')
        ) {
          return null;
        }

        // Skip null errors (often from Uppy file rejection or internal handling)
        if (
          error === null ||
          error?.message === 'null' ||
          error?.message === null
        ) {
          return null;
        }
      }

      // Don't send events without meaningful information
      if (!event.exception && !event.message) {
        return null;
      }

      // Skip events with null or empty error messages
      if (
        event.exception?.values?.[0]?.value === 'null' ||
        event.exception?.values?.[0]?.value === '' ||
        event.exception?.values?.[0]?.value === null
      ) {
        return null;
      }

      // Check exception value for network errors
      const exceptionValue = event.exception?.values?.[0]?.value;
      const exceptionType = event.exception?.values?.[0]?.type;

      // Filter AxiosError with "Network Error" message
      if (
        exceptionType === 'AxiosError' &&
        exceptionValue === 'Network Error'
      ) {
        return null;
      }

      // Filter any exception with "Network Error" in the value
      if (exceptionValue?.includes('Network Error')) {
        return null;
      }

      // Suppress message-type events (e.g. from captureConsoleIntegration logging
      // a stringified AxiosError) that match handled login auth/network patterns.
      // These have no event.exception, so status-based checks above don't apply.
      if (!event.exception && event.message) {
        const msg = event.message;
        const isHandledLoginError =
          msg.includes('Network Error') ||
          msg.includes('ERR_NETWORK') ||
          (msg.includes('AxiosError') &&
            (msg.includes('401') ||
              msg.includes('403') ||
              msg.includes('400') ||
              msg.includes('422'))) ||
          msg.includes('Request failed with status code 401') ||
          msg.includes('Request failed with status code 403');
        if (isHandledLoginError) {
          return null;
        }
      }

      // Check breadcrumbs for console errors matching handled auth/network status codes
      const consoleErrorBreadcrumb = event.breadcrumbs?.find(
        b => b.category === 'console' && b.level === 'error'
      );
      if (
        consoleErrorBreadcrumb?.message?.includes('AxiosError') &&
        (consoleErrorBreadcrumb?.message?.includes('400') ||
          consoleErrorBreadcrumb?.message?.includes('401') ||
          consoleErrorBreadcrumb?.message?.includes('403') ||
          consoleErrorBreadcrumb?.message?.includes('404') ||
          consoleErrorBreadcrumb?.message?.includes('422') ||
          consoleErrorBreadcrumb?.message?.includes('Network Error'))
      ) {
        return null;
      }

      // Check exception value for AxiosError with 400/422
      if (
        exceptionValue?.includes('AxiosError') &&
        (exceptionValue?.includes('400') || exceptionValue?.includes('422'))
      ) {
        return null;
      }

      // Filter "Request failed with status code 422" errors (validation errors from login, forms, document saves)
      if (exceptionValue?.includes('Request failed with status code 422')) {
        return null;
      }

      // Filter AxiosError exceptions by type with 422 status codes
      if (
        exceptionType === 'AxiosError' &&
        exceptionValue?.includes('status code 422')
      ) {
        return null;
      }

      // Filter any AxiosError with common HTTP status codes (catches all formats)
      // These are handled by the UI and don't need to be reported to Sentry
      if (
        exceptionType === 'AxiosError' &&
        (exceptionValue?.includes('401') ||
          exceptionValue?.includes('403') ||
          exceptionValue?.includes('404') ||
          exceptionValue?.includes('422'))
      ) {
        return null;
      }

      // Filter any exception with "Request failed" and common status codes
      if (
        exceptionValue?.includes('Request failed') &&
        (exceptionValue?.includes('401') ||
          exceptionValue?.includes('403') ||
          exceptionValue?.includes('404') ||
          exceptionValue?.includes('422'))
      ) {
        return null;
      }

      // Filter registration validation errors - handled by UI with user feedback
      if (exceptionValue?.includes('Registration could not be completed')) {
        return null;
      }

      // Enrich RestError (Azure/S3 SDK) with code + statusCode so the event is
      // actionable even when the message is empty ("No error message").
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const originalError = hint.originalException as any;
      if (
        exceptionType === 'RestError' ||
        originalError?.name === 'RestError'
      ) {
        if (!event.extra) event.extra = {};
        if (originalError?.code)
          event.extra['restErrorCode'] = originalError.code;
        if (originalError?.statusCode)
          event.extra['restErrorStatusCode'] = originalError.statusCode;
        if (originalError?.details)
          event.extra['restErrorDetails'] = JSON.stringify(
            originalError.details
          ).slice(0, 500);
      }

      return event;
    },
    // Before send transaction - filter out non-error transactions
    beforeSendTransaction(transaction) {
      // Only send transactions that have errors or are very slow
      const hasError = transaction.contexts?.trace?.status !== 'ok';
      const isSlow =
        (transaction.contexts?.trace?.data?.['duration'] as number) > 10000; // 10 seconds

      if (!hasError && !isSlow) {
        return null; // Don't send successful, fast transactions
      }
      return transaction;
    },
    // Enable logs to be sent to Sentry
    enableLogs: true,
    // Maximum breadcrumbs to capture
    maxBreadcrumbs: 50,
    // Attach stack traces to messages
    attachStacktrace: true,
    // Environment
    environment: import.meta.env.VITE_NODE_ENV || 'production',
    // Performance monitoring
    profilesSampleRate: 0 // Disable profiling to save costs
  });
}

// Installed after Sentry.init so the recovery reports land, and before the
// generic handlers below so a stale-chunk failure is reloaded away instead of
// being swallowed as a filtered dynamic-import error.
installStaleBuildRecovery();

window.addEventListener(
  'unhandledrejection',
  (event: PromiseRejectionEvent) => {
    if (import.meta.env.VITE_NODE_ENV === 'production') {
      const error = event.reason;
      // Drop non-Error rejections of plain `{code, message}` or similar
      // "API error object" shapes before Sentry synthesizes a title for
      // them. Browser extensions (password managers, anti-fraud scripts)
      // injected into the login form reject this way on their own
      // backend errors. Legitimate app code throws Errors.
      if (
        error &&
        typeof error === 'object' &&
        !(error instanceof Error) &&
        !error.stack
      ) {
        return;
      }
      // Filter out expected errors
      const errorMessage = error?.message || String(error);
      const errorName = error?.name || '';
      if (
        errorMessage.includes('401') ||
        errorMessage.includes('402') ||
        errorMessage.includes('403') ||
        errorMessage.includes('404') ||
        errorMessage.includes('409') ||
        errorMessage.includes('422') ||
        errorMessage.includes('429') ||
        errorMessage.includes('Network Error') ||
        errorMessage.includes('ERR_NETWORK') ||
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('companion.') ||
        (errorMessage.includes('timeout') &&
          errorMessage.includes('exceeded')) ||
        errorMessage.includes('Request aborted') ||
        // React DOM synchronization errors
        errorName === 'NotFoundError' ||
        errorMessage.includes("Failed to execute 'removeChild'") ||
        errorMessage.includes('is not a child of this node')
      ) {
        return; // Don't log to Sentry
      }
      Sentry.captureException(error);
    }
  }
);

window.addEventListener('error', (event: ErrorEvent) => {
  if (import.meta.env.VITE_NODE_ENV === 'production') {
    const error = event.error;
    // Filter out expected errors
    const errorMessage = error?.message || String(error);
    const errorName = error?.name || '';
    if (
      errorMessage.includes('401') ||
      errorMessage.includes('402') ||
      errorMessage.includes('403') ||
      errorMessage.includes('404') ||
      errorMessage.includes('409') ||
      errorMessage.includes('422') ||
      errorMessage.includes('429') ||
      errorMessage.includes('Network Error') ||
      errorMessage.includes('ERR_NETWORK') ||
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('companion.') ||
      (errorMessage.includes('timeout') && errorMessage.includes('exceeded')) ||
      errorMessage.includes('Request aborted') ||
      // React DOM synchronization errors
      errorName === 'NotFoundError' ||
      errorMessage.includes("Failed to execute 'removeChild'") ||
      errorMessage.includes('is not a child of this node')
    ) {
      return; // Don't log to Sentry
    }
    Sentry.captureException(error);
  }
});

function RouterErrorFallback() {
  /* eslint-disable no-restricted-syntax -- Error boundary must use inline styles since theme context is unavailable */
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        color: '#000',
        padding: '20px',
        textAlign: 'center',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
    >
      <h1 style={{ fontSize: '36px', fontWeight: 'bold', margin: '0 0 8px' }}>
        Something went wrong.
      </h1>
      <p style={{ fontSize: '18px', marginBottom: '20px', color: '#6b7280' }}>
        A client-side error occurred. Please refresh the page or go back to the
        home page.
      </p>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={() => window.location.reload()}
          type="button"
          style={{
            padding: '10px 24px',
            fontSize: '16px',
            fontWeight: 500,
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            backgroundColor: '#fff',
            color: '#374151',
            cursor: 'pointer'
          }}
        >
          Refresh
        </button>
        <button
          onClick={() => {
            window.location.href = '/';
          }}
          type="button"
          style={{
            padding: '10px 24px',
            fontSize: '16px',
            fontWeight: 500,
            border: 'none',
            borderRadius: '6px',
            backgroundColor: '#4f46e5',
            color: '#fff',
            cursor: 'pointer'
          }}
        >
          Home
        </button>
      </div>
    </div>
  );
  /* eslint-enable no-restricted-syntax */
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="*" element={<App />} errorElement={<RouterErrorFallback />} />
  )
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* eslint-disable no-restricted-syntax -- Error boundary must use inline styles since theme context is unavailable */}
    <Sentry.ErrorBoundary
      fallback={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#f3f4f6',
            color: '#000',
            padding: '20px',
            textAlign: 'center',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          <h1
            style={{ fontSize: '36px', fontWeight: 'bold', margin: '0 0 8px' }}
          >
            Something went wrong.
          </h1>
          <p
            style={{ fontSize: '18px', marginBottom: '20px', color: '#6b7280' }}
          >
            An unexpected error occurred. Please refresh the page or go back to
            the home page.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => window.location.reload()}
              type="button"
              style={{
                padding: '10px 24px',
                fontSize: '16px',
                fontWeight: 500,
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#fff',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              Refresh
            </button>
            <button
              onClick={() => {
                window.location.href = '/';
              }}
              type="button"
              style={{
                padding: '10px 24px',
                fontSize: '16px',
                fontWeight: 500,
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#4f46e5',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              Home
            </button>
          </div>
        </div>
      }
    >
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
    {/* eslint-enable no-restricted-syntax */}
  </React.StrictMode>
);
